'use client';

import { Toaster as Sonner, ToasterProps, toast as sonnerToast } from 'sonner';
import { cn } from '../lib/utils';
import { FaIcon } from './icon';

// Sonner stacks a second toast behind the first at 0.95 scale until the
// pointer enters the list, which leaves it unreadable on a page that raises
// two at once. Expanding by default keeps both legible without a hover a
// touch user cannot perform. A caller that wants the stack can pass
// `expand={false}`.
export function Toaster({ ...props }: ToasterProps) {
   return <Sonner className="toaster group" expand {...props} />;
}

type ToastPosition = NonNullable<ToasterProps['position']>;

type ToastOptions = Omit<ToastProps, 'id' | 'title' | 'variant'> & {
   id?: string | number;
   position?: ToastPosition;
   onAutoClose?: () => void;
   onDismiss?: () => void;
};

type CustomToastProps = Omit<ToastProps, 'id'> &
   Pick<ToastOptions, 'position' | 'onAutoClose' | 'onDismiss'>;

export const toast = {
   dismiss: sonnerToast.dismiss,
   custom: (props: CustomToastProps) =>
      sonnerToast.custom(id => <Toast id={id} {...props} />, sonnerOptions(props)),
   error: (title: string, options?: ToastOptions) =>
      sonnerToast.custom(
         id => <Toast {...options} id={id} title={title} variant="error" />,
         sonnerOptions(options)
      ),
   success: (title: string, options?: ToastOptions) =>
      sonnerToast.custom(
         id => <Toast {...options} id={id} title={title} variant="success" />,
         sonnerOptions(options)
      ),
   warning: (title: string, options?: ToastOptions) =>
      sonnerToast.custom(
         id => <Toast {...options} id={id} title={title} variant="warning" />,
         sonnerOptions(options)
      ),
};

// Only keys the caller set: sonner spreads these over the id it generated
// for the toast, so an explicit `id: undefined` leaves the rendered toast
// with a different id than the one its action button dismisses.
function sonnerOptions(options?: ToastOptions) {
   const picked: Record<string, unknown> = {};
   for (const key of ['id', 'duration', 'position', 'onAutoClose', 'onDismiss'] as const) {
      if (options?.[key] !== undefined) picked[key] = options[key];
   }
   return picked;
}

// A page whose toasts belong in a different corner from the Toaster's own binds
// the position once here, so every call site keeps reading `toast.success(...)`
// and a new one cannot forget it. Sonner renders one list per position, so the
// single Toaster on the page still shows them where the page asked.
export function toastAt(position: ToastPosition) {
   return {
      dismiss: toast.dismiss,
      custom: (props: Omit<ToastProps, 'id'>) => toast.custom({ ...props, position }),
      error: (title: string, options?: ToastOptions) =>
         toast.error(title, { ...options, position }),
      success: (title: string, options?: ToastOptions) =>
         toast.success(title, { ...options, position }),
      warning: (title: string, options?: ToastOptions) =>
         toast.warning(title, { ...options, position }),
   };
}

interface ToastProps {
   id: string | number;
   title: string;
   description?: string;
   action?: {
      label: string;
      onClick: () => void;
   };
   cancel?: {
      label: string;
      onClick: () => void;
   };
   variant?: 'default' | 'error' | 'success' | 'warning';
   duration?: number;
   'data-testid'?: string;
}

function Toast({
   id,
   title,
   description,
   action,
   cancel,
   variant = 'default',
   'data-testid': dataTestId,
}: ToastProps) {
   return (
      <div
         className={cn(
            'flex w-full max-w-md rounded-xl border p-4 shadow-lg',
            variant === 'error' && 'bg-red-50 border-red-200',
            variant === 'success' && 'bg-green-50 border-green-200',
            variant === 'warning' && 'bg-amber-50 border-amber-200',
            variant === 'default' && 'bg-gray-50 border-gray-200'
         )}
         data-testid={dataTestId}
      >
         <div className="flex">
            <div className="flex-shrink-0">
               <FaIcon
                  name={
                     {
                        default: 'circle-info',
                        error: 'triangle-exclamation',
                        success: 'circle-check',
                        warning: 'triangle-exclamation',
                     }[variant]
                  }
                  style="solid"
                  className={cn(
                     'size-5',
                     variant === 'error' && 'text-red-500',
                     variant === 'success' && 'text-green-500',
                     variant === 'warning' && 'text-amber-500',
                     variant === 'default' && 'text-brand-500'
                  )}
               />
            </div>
            <div className="ml-3 flex-1">
               <p
                  className={cn(
                     'text-sm font-medium',
                     variant === 'error' && 'text-red-900',
                     variant === 'success' && 'text-green-900',
                     variant === 'warning' && 'text-amber-900',
                     variant === 'default' && 'text-gray-900'
                  )}
               >
                  {title}
               </p>
               {description && (
                  <p
                     className={cn(
                        'mt-1 text-sm',
                        variant === 'error' && 'text-red-800',
                        variant === 'success' && 'text-green-800',
                        variant === 'warning' && 'text-amber-800',
                        variant === 'default' && 'text-gray-700'
                     )}
                  >
                     {description}
                  </p>
               )}
               {(action || cancel) && (
                  <div className="mt-3 flex gap-2">
                     {action && (
                        <button
                           data-slot="button"
                           className={cn(
                              'button-xs',
                              variant === 'error' && 'bg-red-600 hover:bg-red-700',
                              variant === 'success' && 'bg-green-600 hover:bg-green-700',
                              variant === 'warning' && 'bg-amber-600 hover:bg-amber-700'
                           )}
                           onClick={e => {
                              // Sonner hands focus back to whatever had it
                              // before the toast as soon as focus leaves the
                              // toast. Give it back first, so an action that
                              // moves focus itself (opening a drawer) is not
                              // undone a moment later.
                              e.currentTarget.blur();
                              action.onClick();
                              sonnerToast.dismiss(id);
                           }}
                        >
                           {action.label}
                        </button>
                     )}
                     {cancel && (
                        <button
                           data-slot="button"
                           className={cn(
                              'button-xs button-ghost',
                              variant === 'error' && 'text-red-700 hover:bg-red-100',
                              variant === 'success' && 'text-green-700 hover:bg-green-100',
                              variant === 'warning' && 'text-amber-700 hover:bg-amber-100'
                           )}
                           onClick={() => {
                              cancel.onClick();
                              sonnerToast.dismiss(id);
                           }}
                        >
                           {cancel.label}
                        </button>
                     )}
                  </div>
               )}
            </div>
         </div>
      </div>
   );
}
